import { useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { getDefaultActionItemHandlers } from '../utils';
import { config } from '@config/pendo.config.json';

// Redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import {
  resetMentoringProgramSessionsList,
  updateMentoringProgramSession,
} from 'redux/actions/mentoring-program-sessions';
import { IntegrationAuth, MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import {
  ActionItemOwner,
  MentoringProgramActionItem,
} from 'redux/schemas/models/mentoring-program-action-items';
import { getSessionActionItems } from 'redux/selectors/mentorship-program-sessions';

// Components
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvAthenaFroala, { ResizeType } from 'athena/components/nv-athena-froala';
import SaveToast from 'athena/components/mentoring-program/program-configuration/save-toast';
import NvDropdown, {
  NvDropdownButtonStyle,
  NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import ActionItem from 'athena/components/action-item';
import ScheduleInfoCard from './schedule-info-card';

import { ToolbarOptions } from 'froala/helpers/nv-froala-constants';
import { DEFAULT_ACTION_ITEM } from 'athena/components/action-item/constants';
import PulseCheckCTA from '../next-steps-panel/session-pulse-check/pulse-check-cta';
import useCalendarIntegration from 'athena/hooks/use-calendar-integration';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { selectGuidesForSessionByRole } from 'redux/selectors/mentorship-program-guides';
import GuidanceSection from '../guidance-section';
import { css } from '@emotion/react';

const pulseCheckStyles = css`
  top: 0 !important;
`;

type SessionViewModeProps = {
  session: MentorshipProgramSession;
  connection: any;
  setViewMode: (isViewMode: boolean) => void;
  shouldShowPulseCheckCTA: boolean;
  openSessionPulseCheck: (sessionId: number) => void;
  onUpdateSession?: (session: MentorshipProgramSession) => void;
  integrations?: IntegrationAuth;
  openIntegrationsModal?: () => void;
  deleteSession: () => void;
};

const SessionViewMode = (props: SessionViewModeProps) => {
  const {
    session,
    connection,
    setViewMode,
    onUpdateSession,
    integrations,
    openIntegrationsModal,
    deleteSession,
  } = props;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout;
  const dispatch = useAppDispatch();
  const connectionId = connection?.connectionId;
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentoringProgramId,
    mentorshipProgramEnrollment: enrollmentId,
    guidanceEnabled,
  } = mentoringProgram || {};
  const [sessionDetails, setSessionDetails] = useState(session?.details);
  const mentoringProgramEnrollment: MentoringProgramEnrollment = useSelector(state => (
    state.models.mentoringProgramEnrollments[enrollmentId]
  ));
  const { role } = mentoringProgramEnrollment || {};
  const guides = useSelector(selectGuidesForSessionByRole(role.name, session?.id));

  useEffect(() => {
    if (session) {
      setSessionDetails(session.details)
    }
  }, [session]);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>();
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const sessionActionItems: MentoringProgramActionItem[] = useSelector(getSessionActionItems(session?.id));
  const [liveMessage, setLiveMessage] = useState('');

  const getDefaultActionItem = () => ({
    ...DEFAULT_ACTION_ITEM,
    assignedTo: mentoringProgramEnrollment,
    owner: { id: session?.id, type: ActionItemOwner.SESSION },
  });
  const connectionEnrollments = {
    [mentoringProgramEnrollment.id]: mentoringProgramEnrollment,
    [connection?.assignedEnrollment?.id]: connection?.assignedEnrollment,
  };

  const { onAdd, onUpdate, onDelete } = getDefaultActionItemHandlers(mentoringProgramId, dispatch)

  const deleteSessionOption: NvDropdownOption = {
    type: 'text',
    class: 'text-danger',
    text: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.OPTIONS.DELETE_SESSION(),
    callback: deleteSession,
  };

  const configureIntegrationsOption: NvDropdownOption = {
    type: 'text',
    text: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.OPTIONS.CONFIGURE_INTEGRATIONS(),
    callback: openIntegrationsModal,
  };

  const optionItems: NvDropdownOption[] = [];

  const { hasMs365CalendarIntegration } = useCalendarIntegration();
  const hasIntegrationsEnabled = integrations?.source?.length > 0 || hasMs365CalendarIntegration;

  if (hasIntegrationsEnabled) {
    optionItems.push(configureIntegrationsOption);
  }
  optionItems.push(deleteSessionOption);

  const handleEdit = () => {
    setSaveStatus(SaveStatus.IN_PROGRESS);
    dispatch(updateMentoringProgramSession({
      ...session,
      programId: mentoringProgram.id,
      details: sessionDetails,
      sessionId: session?.id,
      showAlertMessage: false,
    })).then(() => {
      setSaveStatus(SaveStatus.COMPLETED);
      dispatch(resetMentoringProgramSessionsList({ resetList: true }));
      onUpdateSession?.({ ...session, details: sessionDetails });
    }).finally(() => {
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    });
  };

  const handleChange = (value) => {
    setSessionDetails(value);
  };

  return (
    <>
      <div className='header'>
        <div>{t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.HEADER()}</div>
        <div className='d-flex'>
          {saveStatus ? <SaveToast status={saveStatus} /> : null}
          <NvDropdown
            buttonStyle={NvDropdownButtonStyle.ICON}
            iconClass='icon-more icon-small mr-6 ml-4 text-gray-2'
            items={optionItems}
            toggleDataQa={dataQATag.sessionMenuDropdown}
          />
        </div>
      </div>
      <ScheduleInfoCard session={session} onClick={() => setViewMode(false)} />
      <div
        className='content position-relative'
        css={pulseCheckStyles}
      >
        {props.shouldShowPulseCheckCTA && (
          <PulseCheckCTA
            sessionId={session?.id}
            openSessionPulseCheck={() => props.openSessionPulseCheck(session?.id)}
          />
        )}
        <div className='session-action-items'>{t.MENTORING_PROGRAMS.PARTICIPANT_HOME.ACTION_ITEMS.TITLE()}</div>
        <div className='my-3' data-qa={dataQATag.actionItemsList} data-qa-id={`${dataQATag.actionItemsList}-${session?.id}`}>
          {[
            getDefaultActionItem(),
            ...sessionActionItems,
          ].map(
            (item) => item && (
              <ActionItem
                key={item.id}
                item={item}
                onAdd={onAdd}
                onUpdate={onUpdate}
                onDelete={onDelete}
                isEditing={editingId === item.id}
                setEditingId={setEditingId}
                connectionId={connectionId}
                enrollments={connectionEnrollments}
                setLiveMessage={setLiveMessage}
              />
            ),
          )}
          <div role='alert' aria-live='polite' className='sr-only'>{liveMessage}</div>
        </div>
        <div className='session-details'>{t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.SESSION_DETAILS()}</div>
        <div className='session-details-description'>{t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.SESSION_DETAILS_DESCRIPTION()}</div>
        {guidanceEnabled && guides?.length > 0 ? (
          <GuidanceSection guides={guides} />
        ) : null}
        <NvAthenaFroala
          name='description'
          value={sessionDetails}
          onChange={handleChange}
          onBlur={handleEdit}
          withForm={false}
          resize={ResizeType.VERTICAL}
          placeholder={t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.SESSION_DETAILS_PLACEHOLDER()}
          toolbarButtons={[
            ToolbarOptions.BOLD,
            ToolbarOptions.ITALIC,
            ToolbarOptions.UNDERLINE,
            ToolbarOptions.FORMAT_UL,
            ToolbarOptions.FORMAT_OL,
            ToolbarOptions.INSERT_LINK,
          ]}
        />
      </div>
    </>
  );
};

export default SessionViewMode;
